import { useParams, useNavigate } from "react-router-dom";
import {
  useGetUserByIdQuery,
  useEditUserByIdMutation,
  useDeleteUserByIdMutation,
} from "@/features/api/authApi";
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2, Trash, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading: isFetching } = useGetUserByIdQuery(id, {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  const [editUser, { isLoading: isUpdating }] = useEditUserByIdMutation();
  const [deleteUser] = useDeleteUserByIdMutation();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "user",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (data?.user) {
      const u = data.user;
      setForm({
        name: u.name || "",
        email: u.email || "",
        phone: u.phone || "",
        role: u.role || "user",
        password: "",
      });
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return false;
    }
    if (!form.email.includes("@") || !form.email.includes(".")) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (form.phone && !/^\+?\d{10,15}$/.test(form.phone.replace(/\D/g, ""))) {
      toast.error("Please enter a valid phone number (10–15 digits)");
      return false;
    }
    if (form.password && form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return false;
    }
    if (!["user", "student", "customer", "instructor", "admin"].includes(form.role)) {
      toast.error("Please select a valid role");
      return false;
    }
    return true;
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validateForm()) return;

  try {
    const payload = { ...form };

    // 🔥 IMPORTANT: don't send empty password
    if (!payload.password) {
      delete payload.password;
    }

    console.log("Updating user payload:", payload);

    await editUser({
      id,
       data: payload,   // ✅ FLATTEN PAYLOAD
    }).unwrap();

    toast.success("User updated successfully.");
    navigate("/admin/users");
  } catch (err) {
    console.error("Update failed:", err);
    toast.error(err?.data?.message || "Failed to update user.");
  }
};


  const handleDelete = async () => {
    if (form.role === "admin") {
      toast.error("Admin users cannot be deleted.");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await deleteUser(id).unwrap();
      toast.success("User deleted successfully.");
      navigate("/admin/users");
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error(err?.data?.message || "Failed to delete user.");
    }
  };

  if (isFetching) {
    return (
      <div className="flex justify-center mt-10">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <Card className="p-4 max-w-2xl mx-auto mt-10 relative">
      {form.role !== "admin" && (
        <Button
          size="icon"
          variant="destructive"
          className="absolute top-4 right-4"
          onClick={handleDelete}
          disabled={isUpdating}
        >
          <Trash size={18} />
        </Button>
      )}

      <CardHeader>
        <CardTitle>Edit User</CardTitle>
        <CardDescription>Update user details and credentials</CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Enter user name"
              required
              disabled={isUpdating}
            />
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Enter user email"
              required
              disabled={isUpdating}
            />
          </div>

          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="Enter phone number (e.g., +911234567890)"
              disabled={isUpdating}
            />
          </div>

          <div>
            <Label htmlFor="role">Role</Label>
            <select
              id="role"
              name="role"
              value={form.role}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring focus:ring-blue-500"
              disabled={isUpdating}
            >
              <option value="user">User</option>
              <option value="student">Student</option>
              <option value="customer">Customer</option>
              <option value="instructor">Instructor</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="relative">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={handleChange}
              placeholder="Leave blank to keep current password"
              className="pr-10"
              disabled={isUpdating}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-3 top-[38px] text-gray-500 hover:text-black"
              disabled={isUpdating}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <div className="flex justify-between items-center mt-6">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/users")}
              disabled={isUpdating}
            >
              Back
            </Button>
            <Button type="submit" disabled={isUpdating}>
              {isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default EditUser;
